import { Button, Drawer, EmptyState, Illustration, LogoMark, toast } from '@tomo/ui';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router';
import { SessionProvider, useProject, useProjectLoader, useSession, useSyncState } from '../../data/hooks.tsx';
import { AuthDialog, type AuthMode } from '../account/AuthDialog.tsx';
import { useHotkey } from '../../lib/hotkeys.ts';
import { useStore } from '../../lib/store.ts';
import { NewCollectionDialog } from '../collections/NewCollectionDialog.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import type { LaunchState } from '../projects/Launch.tsx';
import { NewProjectDialog } from '../projects/NewProjectDialog.tsx';
import { useWindowsValue } from '../windows/hooks.ts';
import { Taskbar } from '../windows/Taskbar.tsx';
import { WindowDesk } from '../windows/WindowDesk.tsx';
import { activeRecordId } from '../windows/windows.ts';
import { Sidebar } from './Sidebar.tsx';
import { Topbar } from './Topbar.tsx';
import { TopbarSlotProvider } from './TopbarSlot.tsx';
import { mobileNavOpen, openPalette, sidebarCollapsed, toggleSidebar } from './ui-state.ts';

export function ProjectRoot() {
  const { projectId = '' } = useParams();
  const state = useProjectLoader(projectId);

  if (state.status === 'loading') return <FullscreenLoader label="Opening project…" />;
  if (state.status === 'missing') {
    return (
      <div className="center-screen">
        <EmptyState
          size="lg"
          art={<Illustration name="search" />}
          title="Project not found"
          description="It isn’t on this device. If it’s a cloud project, sign in and open it from the project menu."
          actions={
            <Link to="/" state={{ skip: projectId } satisfies LaunchState}>
              <Button variant="primary">Back to Tomo</Button>
            </Link>
          }
        />
      </div>
    );
  }
  if (state.status === 'waiting-cloud') {
    return (
      <SessionProvider session={state.session}>
        <WaitingForCloud />
      </SessionProvider>
    );
  }
  return (
    <SessionProvider session={state.session}>
      <TopbarSlotProvider>
        <ProjectShell />
      </TopbarSlotProvider>
    </SessionProvider>
  );
}

const WAITING_COPY: Record<'offline' | 'signed-out' | 'forbidden' | 'upgrade-required' | 'error', { title: string; description: string }> = {
  offline: { title: 'You’re offline', description: 'This cloud project isn’t on this device yet. It opens as soon as you’re back online.' },
  'signed-out': { title: 'Sign in to open this project', description: 'It lives in a Tomo Cloud workspace. Sign in with an account that’s a member.' },
  forbidden: { title: 'No access', description: 'You don’t have access to this project.' },
  'upgrade-required': { title: 'Sync is paused', description: 'This workspace needs a Pro plan to sync.' },
  error: { title: 'Couldn’t download the project', description: 'Something went wrong while syncing. Try again in a moment.' },
};

/** A cloud project that isn't on this device yet: download it, or explain why we can't. */
function WaitingForCloud() {
  const session = useSession();
  const sync = useSyncState();
  const [auth, setAuth] = useState<AuthMode | null>(null);
  const status = sync.mode === 'cloud' ? sync.status : 'connecting';
  if (status === 'connecting' || status === 'syncing' || status === 'synced') return <FullscreenLoader label="Downloading project from the cloud…" />;
  const copy = WAITING_COPY[status];
  const signedOut = status === 'signed-out';
  return (
    <div className="center-screen">
      <EmptyState
        size="lg"
        art={<Illustration name="cloud" />}
        title={copy.title}
        description={(status !== 'signed-out' && status !== 'offline' && sync.mode === 'cloud' && sync.message) || copy.description}
        actions={
          <>
            {signedOut && (
              <Button variant="primary" onClick={() => setAuth('signin')}>
                Sign in
              </Button>
            )}
            <Link to="/" state={{ skip: session.id } satisfies LaunchState}>
              <Button variant={signedOut ? 'secondary' : 'primary'}>Open another project</Button>
            </Link>
          </>
        }
      />
      <AuthDialog mode={auth} onClose={() => setAuth(null)} onModeChange={setAuth} />
    </div>
  );
}

export function FullscreenLoader({ label }: { label: string }) {
  return (
    <div className="center-screen">
      <div className="loader-screen">
        <LogoMark size={40} />
        <span>{label}</span>
      </div>
    </div>
  );
}

function ProjectShell() {
  const project = useProject();
  const collapsed = useStore(sidebarCollapsed);
  const mobileOpen = useStore(mobileNavOpen);
  const { pathname } = useLocation();

  useEffect(() => {
    mobileNavOpen.set(false);
  }, [pathname]);

  useHotkey('mod+k', () => openPalette());
  useHotkey('mod+\\', () => toggleSidebar());
  useHotkey(
    'mod+z',
    () => {
      if (project.undoManager.canUndo()) {
        project.undoManager.undo();
        toast({ title: 'Undone', duration: 1500 });
      }
    },
    { allowInEditable: false },
  );
  useHotkey(['mod+shift+z', 'mod+y'], () => project.undoManager.canRedo() && project.undoManager.redo(), { allowInEditable: false });

  return (
    <div className="app" data-sidebar={collapsed ? 'collapsed' : 'open'}>
      <aside className="app__sidebar">
        <Sidebar />
      </aside>
      <main className="app__main">
        <Topbar />
        <div className="app__desk">
          <div className="app__content" id="app-content">
            <Outlet />
          </div>
          <WindowDesk />
        </div>
        <Taskbar />
      </main>
      <PresenceLocation />
      <Drawer open={mobileOpen} onOpenChange={(o) => mobileNavOpen.set(o)} side="left" width={288} label="Navigation">
        <Sidebar onNavigate={() => mobileNavOpen.set(false)} />
      </Drawer>
      <CommandPalette />
      <NewCollectionDialog />
      <NewProjectDialog />
    </div>
  );
}

/** Tells collaborators where we are (drives "who's here" avatars): the record in the active window, else the page. */
function PresenceLocation() {
  const session = useSession();
  const { pathname } = useLocation();
  const record = useWindowsValue(activeRecordId);
  useEffect(() => {
    session.setLocation(record ?? /\/r\/([^/]+)/.exec(pathname)?.[1] ?? pathname.replace(`/p/${session.id}`, ''));
  }, [record, pathname, session]);
  return null;
}
